import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { reportsAPI } from '../services/api';
import './CaseloadToolsReport.css';

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

function formatDate(str) {
  if (!str) return '—';
  const s = String(str).trim();
  const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${Number(match[2])}/${Number(match[3])}/${match[1]}`;
  const d = new Date(str);
  return Number.isNaN(d.getTime()) ? str : `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

function parseDateToNumber(value) {
  if (!value) return null;
  const s = String(value).trim();
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    const n = Date.parse(`${ymd[1]}-${ymd[2]}-${ymd[3]}T00:00:00`);
    return Number.isNaN(n) ? null : n;
  }
  const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (mdy) {
    const year = mdy[3].length === 2 ? `20${mdy[3]}` : mdy[3];
    const n = Date.parse(`${year}-${String(mdy[1]).padStart(2, '0')}-${String(mdy[2]).padStart(2, '0')}T00:00:00`);
    return Number.isNaN(n) ? null : n;
  }
  const d = Date.parse(s);
  return Number.isNaN(d) ? null : d;
}

function phaseLabel(phase) {
  if (phase === 'intake') return 'Intake';
  if (phase === 'active_ifsp') return 'Active IFSP';
  return '—';
}

function csvEscape(value) {
  const str = value == null ? '' : String(value);
  if (/[,"\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}

function staffName(row) {
  if (row.staff_last_name || row.staff_first_name) return [row.staff_last_name, row.staff_first_name].filter(Boolean).join(', ');
  return '—';
}

function readSavedState(tenantCode) {
  try {
    const key = `caseload-tools-state-v2:${tenantCode || 'default'}`;
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
}

function getDefaultFilters() {
  return {
    referral_open_date: { mode: '1m' },
  };
}

function CaseloadToolsReport({ refreshKey = 0, tenantCode = null }) {
  const saved = readSavedState(tenantCode);
  const [pickDay] = useState(saved.pickDay || todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState(Array.isArray(saved.sort) ? saved.sort : [{ key: 'staff_name', dir: 'asc' }]);
  const [filters, setFilters] = useState(() => {
    const base = getDefaultFilters();
    if (saved.filters && typeof saved.filters === 'object') {
      return { ...base, ...saved.filters };
    }
    return base;
  });
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [draftFilters, setDraftFilters] = useState({});
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0, width: 300 });
  const filterButtonRefs = useRef({});

  const columns = useMemo(
    () => [
      { key: 'client_name', label: 'Client', type: 'string', sortable: true, filter: { kind: 'textContains' } },
      { key: 'ei_number', label: 'EI #', type: 'string', sortable: true, filter: { kind: 'textContains' } },
      { key: 'staff_name', label: 'Service coordinator', type: 'string', sortable: true, filter: { kind: 'multiSelect' } },
      { key: 'phase', label: 'Phase', type: 'string', sortable: true, filter: { kind: 'singleSelect', options: [{ value: 'all', label: 'All' }, { value: 'intake', label: 'Intake' }, { value: 'active_ifsp', label: 'Active IFSP' }] } },
      { key: 'referral_open_date', label: 'Referral open', type: 'date', sortable: true, filter: { kind: 'dateRangePreset' } },
    ],
    []
  );

  useEffect(() => {
    const key = `caseload-tools-state-v2:${tenantCode || 'default'}`;
    localStorage.setItem(key, JSON.stringify({ pickDay, sort, filters }));
  }, [tenantCode, pickDay, sort, filters]);

  const runPreview = useCallback(() => {
    setLoading(true);
    setError(null);
    reportsAPI
      .get('caseload-tools', { as_of: pickDay, phase: 'both', open_window: 'all' })
      .then((res) => {
        setData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.response?.data?.error || err?.message || 'Failed to load report');
        setLoading(false);
      });
  }, [pickDay]);

  useEffect(() => {
    runPreview();
  }, [runPreview, refreshKey]);

  const rows = useMemo(() => {
    const intake = data?.intake || [];
    const activeIfsp = data?.activeIfsp || [];
    const intakeRows = intake.map((row) => ({
      ...row,
      phase: 'intake',
      client_name: [row.client_last_name, row.client_first_name].filter(Boolean).join(', ') || '—',
      staff_name: staffName(row),
    }));
    const ifspRows = activeIfsp.map((row) => ({
      ...row,
      phase: 'active_ifsp',
      client_name: [row.client_last_name, row.client_first_name].filter(Boolean).join(', ') || '—',
      staff_name: staffName(row),
    }));
    return [...intakeRows, ...ifspRows];
  }, [data]);

  const facets = useMemo(() => {
    const map = {};
    columns.forEach((col) => {
      if (col.filter?.kind === 'multiSelect') {
        map[col.key] = Array.from(new Set(rows.map((r) => String(r[col.key] || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));
      }
    });
    return map;
  }, [rows, columns]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      for (const col of columns) {
        const filter = filters[col.key];
        if (!filter) continue;
        const raw = row[col.key];
        if (col.filter?.kind === 'textContains') {
          const text = String(filter.text || '').trim().toLowerCase();
          if (text && !String(raw || '').toLowerCase().includes(text)) return false;
        } else if (col.filter?.kind === 'multiSelect') {
          const values = Array.isArray(filter.values) ? filter.values : [];
          if (values.length > 0 && !values.includes(String(raw || '').trim())) return false;
        } else if (col.filter?.kind === 'singleSelect') {
          const value = String(filter.value || 'all');
          if (value !== 'all' && value !== String(raw || '')) return false;
        } else if (col.filter?.kind === 'dateRangePreset') {
          const n = parseDateToNumber(raw);
          if (n == null) return false;
          const mode = filter.mode || '1m';
          let from = filter.from || '';
          let to = filter.to || '';
          if (mode === '1m' || mode === '6m') {
            const base = data?.asOf || pickDay;
            const d = new Date(`${base}T12:00:00Z`);
            d.setMonth(d.getMonth() - (mode === '1m' ? 1 : 6));
            from = d.toISOString().split('T')[0];
            to = base;
          }
          const fromN = parseDateToNumber(from);
          const toN = parseDateToNumber(to);
          if (fromN != null && n < fromN) return false;
          if (toN != null && n > toN) return false;
        }
      }
      return true;
    });
  }, [rows, filters, columns, data, pickDay]);

  const displayedRows = useMemo(() => {
    if (!Array.isArray(sort) || !sort[0]?.key) return filteredRows;
    const key = sort[0].key;
    const dir = sort[0].dir === 'desc' ? -1 : 1;
    const column = columns.find((c) => c.key === key);
    const isDate = column?.type === 'date';
    return [...filteredRows].sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (isDate) return ((parseDateToNumber(av) || 0) - (parseDateToNumber(bv) || 0)) * dir;
      return String(av || '').localeCompare(String(bv || '')) * dir;
    });
  }, [filteredRows, sort, columns]);

  useEffect(() => {
    if (!openFilterKey) return undefined;
    const onPointerDown = (event) => {
      const target = event.target;
      if (target?.closest?.('.caseload-tools-header-filter-wrap')) return;
      if (target?.closest?.('.caseload-tools-filter-popover-portal')) return;
      setOpenFilterKey(null);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpenFilterKey(null);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openFilterKey]);

  useEffect(() => {
    if (!openFilterKey) return undefined;
    const updatePosition = () => {
      const button = filterButtonRefs.current[openFilterKey];
      if (!button) return;
      const rect = button.getBoundingClientRect();
      const openColumn = columns.find((c) => c.key === openFilterKey);
      const width = openColumn?.filter?.kind === 'dateRangePreset' ? 320 : 300;
      const viewportPadding = 8;
      const maxLeft = window.innerWidth - width - viewportPadding;
      const preferredLeft = rect.right + window.scrollX - width;
      const clampedLeft = Math.max(viewportPadding, Math.min(preferredLeft, maxLeft));
      setPopoverPosition({ top: rect.bottom + window.scrollY + 6, left: clampedLeft, width });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [openFilterKey, columns]);

  const handleSortClick = (key) => {
    setSort((prev) => {
      const current = Array.isArray(prev) && prev[0]?.key === key ? prev[0] : null;
      if (!current) return [{ key, dir: 'asc' }];
      if (current.dir === 'asc') return [{ key, dir: 'desc' }];
      return [];
    });
  };

  const openFilter = (key, buttonElement) => {
    setDraftFilters((prev) => ({ ...prev, [key]: filters[key] || {} }));
    if (buttonElement) filterButtonRefs.current[key] = buttonElement;
    setOpenFilterKey((prev) => (prev === key ? null : key));
  };

  const applyFilter = (column) => {
    const key = column.key;
    const kind = column.filter?.kind;
    const draft = draftFilters[key] || {};
    const nextFilters = { ...filters };
    if (kind === 'textContains') {
      const text = String(draft.text || '').trim();
      if (text) nextFilters[key] = { text };
      else delete nextFilters[key];
    } else if (kind === 'multiSelect') {
      const values = Array.isArray(draft.values) ? draft.values.filter(Boolean) : [];
      if (values.length > 0) nextFilters[key] = { values };
      else delete nextFilters[key];
    } else if (kind === 'singleSelect') {
      const value = draft.value || 'all';
      if (value && value !== 'all') nextFilters[key] = { value };
      else delete nextFilters[key];
    } else if (kind === 'dateRangePreset') {
      const mode = draft.mode || '1m';
      const from = draft.from || '';
      const to = draft.to || '';
      if (mode === 'custom') {
        if (from || to) nextFilters[key] = { mode, from, to };
        else delete nextFilters[key];
      } else {
        nextFilters[key] = { mode };
      }
    }
    setFilters(nextFilters);
    setOpenFilterKey(null);
  };

  const clearFilter = (column) => {
    const key = column.key;
    setFilters((prev) => {
      const next = { ...prev };
      if (key === 'referral_open_date') next[key] = { mode: '1m' };
      else delete next[key];
      return next;
    });
    setDraftFilters((prev) => ({ ...prev, [key]: {} }));
    setOpenFilterKey(null);
  };

  const renderFilterControl = (column) => {
    const kind = column.filter?.kind;
    const value = draftFilters[column.key] || {};
    if (kind === 'textContains') {
      return <input type="text" value={value.text || ''} onChange={(e) => setDraftFilters((prev) => ({ ...prev, [column.key]: { text: e.target.value } }))} placeholder={`Search ${column.label.toLowerCase()}`} className="caseload-tools-filter-input" />;
    }
    if (kind === 'multiSelect') {
      const options = facets[column.key] || [];
      const selectedValues = Array.isArray(value.values) ? value.values : [];
      return (
        <select multiple value={selectedValues} onChange={(e) => setDraftFilters((prev) => ({ ...prev, [column.key]: { values: Array.from(e.target.selectedOptions).map((o) => o.value) } }))} className="caseload-tools-filter-select">
          {options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      );
    }
    if (kind === 'singleSelect') {
      return (
        <select value={value.value || 'all'} onChange={(e) => setDraftFilters((prev) => ({ ...prev, [column.key]: { value: e.target.value } }))} className="caseload-tools-filter-input">
          {column.filter.options.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      );
    }
    if (kind === 'dateRangePreset') {
      const mode = value.mode || '1m';
      return (
        <div className="caseload-tools-filter-date-range">
          <label className="caseload-tools-filter-date-field">
            <span>Window</span>
            <select value={mode} onChange={(e) => setDraftFilters((prev) => ({ ...prev, [column.key]: { ...value, mode: e.target.value } }))} className="caseload-tools-filter-input">
              <option value="1m">Last month</option>
              <option value="6m">Last 6 months</option>
              <option value="custom">Custom date range</option>
            </select>
          </label>
          {mode === 'custom' && (
            <>
              <label className="caseload-tools-filter-date-field">
                <span>From</span>
                <input type="date" value={value.from || ''} onChange={(e) => setDraftFilters((prev) => ({ ...prev, [column.key]: { ...value, mode: 'custom', from: e.target.value } }))} className="caseload-tools-filter-input" />
              </label>
              <label className="caseload-tools-filter-date-field">
                <span>To</span>
                <input type="date" value={value.to || ''} onChange={(e) => setDraftFilters((prev) => ({ ...prev, [column.key]: { ...value, mode: 'custom', to: e.target.value } }))} className="caseload-tools-filter-input" />
              </label>
            </>
          )}
        </div>
      );
    }
    return null;
  };

  const handleReset = () => {
    setSort([{ key: 'staff_name', dir: 'asc' }]);
    setFilters(getDefaultFilters());
  };

  const handleExportCsv = () => {
    const headers = ['Client', 'EI #', 'Service coordinator', 'Phase', 'Referral open'];
    const lines = [headers.map(csvEscape).join(',')];
    displayedRows.forEach((row) => {
      lines.push([row.client_name || '', row.ei_number || '', row.staff_name || '', phaseLabel(row.phase), formatDate(row.referral_open_date)].map(csvEscape).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'caseload_tools.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const filterSummary = columns.map((col) => {
    const filter = filters[col.key];
    if (!filter && col.key !== 'phase' && col.key !== 'referral_open_date') return null;
    if (col.filter?.kind === 'textContains') return `${col.label}: ${filter.text}`;
    if (col.filter?.kind === 'multiSelect') return `${col.label}: ${(filter.values || []).join(', ')}`;
    if (col.filter?.kind === 'singleSelect') return `${col.label}: ${filter?.value === 'all' || !filter?.value ? 'All' : phaseLabel(filter.value)}`;
    if (col.filter?.kind === 'dateRangePreset') {
      const mode = filter?.mode || '1m';
      if (mode === 'custom') return `${col.label}: ${filter?.from || 'Any'} to ${filter?.to || 'Any'}`;
      return `${col.label}: ${mode === '6m' ? 'Last 6 months' : 'Last month'}`;
    }
    return null;
  }).filter(Boolean);

  const openColumn = columns.find((c) => c.key === openFilterKey);
  const filterPopover = openColumn ? createPortal(
    <div className="caseload-tools-filter-popover caseload-tools-filter-popover-portal no-print" style={{ top: `${popoverPosition.top}px`, left: `${popoverPosition.left}px`, width: `${popoverPosition.width}px` }}>
      {renderFilterControl(openColumn)}
      <div className="caseload-tools-filter-popover-actions">
        <button type="button" className="btn btn-secondary" onClick={() => clearFilter(openColumn)}>Clear</button>
        <button type="button" className="btn btn-primary" onClick={() => applyFilter(openColumn)}>Apply</button>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className="caseload-tools-report">
      {error && <div className="caseload-tools-report error">{error}</div>}
      {loading && !data && <div className="caseload-tools-report loading">Loading report…</div>}

      {data && (
        <div className="caseload-tools-printable report-printable">
          <div className="caseload-tools-print-first-block">
            <header className="caseload-tools-header">
              <h3 className="caseload-tools-title">Caseload Calculation Tools (Intake, Active IFSP, etc){tenantCode ? ` – ${tenantCode}` : ''}</h3>
              <p className="caseload-tools-as-of">As of {formatDate(data.asOf || pickDay)}</p>
              <p className="caseload-tools-summary">Intake: {data.summary?.intake || 0} · Active IFSP: {data.summary?.activeIfsp || 0} · Showing: {displayedRows.length}</p>
            </header>
          </div>

          <div className="caseload-tools-toolbar no-print">
            <button type="button" onClick={handleReset} className="btn btn-outline caseload-tools-print-btn">Reset</button>
            <button type="button" onClick={handleExportCsv} className="btn btn-outline caseload-tools-print-btn">Export CSV</button>
            <button type="button" onClick={() => window.print()} className="btn btn-primary caseload-tools-print-btn">Print</button>
          </div>

          {filterSummary.length > 0 && <p className="caseload-tools-filter-summary">Filters: {filterSummary.join(' | ')}</p>}

          <section className="caseload-tools-section">
            {displayedRows.length === 0 ? (
              <p className="caseload-tools-empty">No matching caseload rows.</p>
            ) : (
              <table className="caseload-tools-table">
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th key={col.key} onClick={() => col.sortable && handleSortClick(col.key)} className={col.sortable ? 'caseload-tools-sortable' : ''}>
                        <div className="caseload-tools-header-cell">
                          <span className="caseload-tools-header-label">{col.label}{sort[0]?.key === col.key ? (sort[0].dir === 'desc' ? ' ↓' : ' ↑') : ''}</span>
                          {col.filter && (
                            <div className="caseload-tools-header-filter-wrap no-print" onClick={(e) => e.stopPropagation()}>
                              <button type="button" className="caseload-tools-filter-trigger" onClick={(e) => openFilter(col.key, e.currentTarget)} aria-label={`Filter ${col.label}`}>
                                <svg className="caseload-tools-filter-trigger-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                  <path d="M4 6H20L14 13V18L10 20V13L4 6Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </button>
                            </div>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayedRows.map((row) => (
                    <tr key={row.referral_id}>
                      <td>{row.client_name || '—'}</td>
                      <td>{row.ei_number || '—'}</td>
                      <td>{row.staff_name || '—'}</td>
                      <td>{phaseLabel(row.phase)}</td>
                      <td>{formatDate(row.referral_open_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </div>
      )}
      {filterPopover}
    </div>
  );
}

export default CaseloadToolsReport;
